/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import add from 'date-fns/add';
import AppDatePicker from '../../../shared/components/app-date-picker/AppDatePicker';
import {
  formatDate,
  getNewDate,
  isEmpty,
} from '../../../shared/helper-utils/appUtils';
import {
  API_DATE_FORMAT,
  TODAY_DATE,
} from '../../../shared/constants/Constants';
import AppButton from '../../../shared/components/app-button/AppButton';
import { getFlightSeriesListForSeriesOwner } from '../../../api/flightApi';
import { getUserAgentId } from '../../../shared/helper-utils/localStorage';
import AppAlert from '../../../shared/components/app-alert/AppAlert';
import AppLoading from '../../../shared/components/app-loading/AppLoading';
import AppTextInput from '../../../shared/components/app-text-input/AppTextInput';
import { debounce } from '../../../api/apiUtils';

function SeriesTransactions({ defaultList }) {
  const [fromDate, setFromDate] = useState(getNewDate(TODAY_DATE));
  const [toDate, setToDate] = useState(
    add(getNewDate(TODAY_DATE), { days: 1 }),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(undefined);
  const allResultsRef = useRef(defaultList || []);
  const [txList, setTxList] = useState(defaultList || []);
  const [shownResults, setShownResults] = useState(defaultList.slice(0, 20));
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortParam, setSortParam] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    allResultsRef.current = defaultList || [];
    setShownResults(defaultList.slice(0, 20));
    setTxList(defaultList || []);
  }, [defaultList]);

  const appendResults = (pageNumber, results) => {
    const startindex = pageNumber * 20;
    const endIndex = startindex + 20;
    let newResults;
    if (startindex > results.length) {
      return;
    }
    if (endIndex > results.length) {
      newResults = results.slice(0, results.length);
    } else {
      newResults = results.slice(0, endIndex);
    }
    setShownResults(newResults);
  };

  useEffect(() => {
    appendResults(page, txList);
  }, [page]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollY } = window;
      const pageHeight =
        document.documentElement.scrollHeight - window.innerHeight - 100;
      // const scrollThreshold = 100;
      if (scrollY >= pageHeight) {
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSort = (sortBy, txArr) => {
    let param = sortBy;
    let order = 'asc';
    if (txArr === undefined) {
      if (param === sortParam) {
        order = sortOrder === 'asc' ? 'desc' : 'asc';
      }
      setSortParam(param);
      setSortOrder(order);
    } else {
      order = sortOrder;
      param = sortParam;
    }
    let list;
    if (txArr) {
      list = [...txArr];
    } else {
      list = [...txList];
    }
    if (param === 'txId') {
      if (order === 'asc') {
        list.sort((a, b) => a.TxId.localeCompare(b.TxId));
      } else {
        list.sort((a, b) => b.TxId.localeCompare(a.TxId));
      }
    }
    if (param === 'paxName') {
      if (order === 'asc') {
        list.sort((a, b) =>
          `${a.Title} ${a.FirstName} ${a.LastName}`.localeCompare(
            `${b.Title} ${b.FirstName} ${b.LastName}`,
          ),
        );
      } else {
        list.sort((a, b) =>
          `${b.Title} ${b.FirstName} ${b.LastName}`.localeCompare(
            `${a.Title} ${a.FirstName} ${a.LastName}`,
          ),
        );
      }
    }
    if (param === 'flightNumber') {
      if (order === 'asc') {
        list.sort((a, b) =>
          `${a.Carrier}-${a.FlightNumber}`.localeCompare(
            `${b.Carrier}-${b.FlightNumber}`,
          ),
        );
      } else {
        list.sort((a, b) =>
          `${b.Carrier}-${b.FlightNumber}`.localeCompare(
            `${a.Carrier}-${a.FlightNumber}`,
          ),
        );
      }
    }
    if (param === 'sector') {
      if (order === 'asc') {
        list.sort((a, b) =>
          `${a.Origin}-${a.Destination}`.localeCompare(
            `${b.Origin}-${b.Destination}`,
          ),
        );
      } else {
        list.sort((a, b) =>
          `${b.Origin}-${b.Destination}`.localeCompare(
            `${a.Origin}-${a.Destination}`,
          ),
        );
      }
    }
    if (param === 'journeyDate') {
      if (order === 'asc') {
        list.sort((a, b) =>
          new Date(a.JourneyDate) > new Date(b.JourneyDate) ? 1 : -1,
        );
      } else {
        list.sort((a, b) =>
          new Date(b.JourneyDate) > new Date(a.JourneyDate) ? 1 : -1,
        );
      }
    }
    if (param === 'depTime') {
      if (order === 'asc') {
        list.sort((a, b) => (a.DepatureTime > b.DepatureTime ? 1 : -1));
      } else {
        list.sort((a, b) => (b.DepatureTime > a.DepatureTime ? 1 : -1));
      }
    }
    if (param === 'bookingStatus') {
      if (order === 'asc') {
        list.sort((a, b) => a.BookingStatus.localeCompare(b.BookingStatus));
      } else {
        list.sort((a, b) => b.BookingStatus.localeCompare(a.BookingStatus));
      }
    }
    if (param === 'bookingDate') {
      if (order === 'asc') {
        list.sort((a, b) =>
          new Date(a.BookingDate) > new Date(b.BookingDate) ? 1 : -1,
        );
      } else {
        list.sort((a, b) =>
          new Date(b.BookingDate) > new Date(a.BookingDate) ? 1 : -1,
        );
      }
    }
    if (param === 'totalAmount') {
      if (order === 'asc') {
        list.sort((a, b) => (a.TotalAmount > b.TotalAmount ? 1 : -1));
      } else {
        list.sort((a, b) => (b.TotalAmount > a.TotalAmount ? 1 : -1));
      }
    }
    if (param === 'pnr') {
      if (order === 'asc') {
        list.sort((a, b) => a.PNR.localeCompare(b.PNR));
      } else {
        list.sort((a, b) => b.PNR.localeCompare(a.PNR));
      }
    }
    if (param === 'paxType') {
      if (order === 'asc') {
        list.sort((a, b) => a.PaxType.localeCompare(b.PaxType));
      } else {
        list.sort((a, b) => b.PaxType.localeCompare(a.PaxType));
      }
    }
    if (param === 'dob') {
      if (order === 'asc') {
        list.sort((a, b) => (a.DOB > b.DOB ? 1 : -1));
      } else {
        list.sort((a, b) => (b.DOB > a.DOB ? 1 : -1));
      }
    }
    setShownResults(list.slice(0, 20));
    setPage(0);
    setTxList(list);
  };

  const setSearchResults = debounce((val) => {
    try {
      const data = [...allResultsRef.current];
      const enhancedData = data.map((item) => ({
        ...item,
        fullname:
          `${item.Title} ${item.FirstName} ${item.LastName}`.toLowerCase(),
        flightCarrierWithNumber:
          `${item.Carrier}-${item.FlightNumber}`.toLowerCase(),
        sectorDetails: `${item.Origin}-${item.Destination}`.toLowerCase(),
      }));
      const criteria = val
        .toLowerCase()
        .split(',')
        .map((c) => c.trim());
      const filtered = enhancedData.filter((item) =>
        criteria.every((criterion) =>
          Object.keys(item).some((key) =>
            String(item[key]).toLowerCase().includes(criterion),
          ),
        ),
      );

      handleSort('', filtered);
    } catch (error) {
      // return;
    }
  }, 500);

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchText(value);
    setSearchResults(value);
  };

  const handleGetSeriesTransactions = async () => {
    setIsLoading(true);
    try {
      const res = await getFlightSeriesListForSeriesOwner(
        formatDate(getNewDate(fromDate), API_DATE_FORMAT),
        formatDate(getNewDate(toDate), API_DATE_FORMAT),
        getUserAgentId(),
      );
      setIsLoading(false);
      if (res && res.Root && res.Root.Status === 'OK') {
        if (
          !isEmpty(res.Root.Result) &&
          !isEmpty(res.Root.Result.SeriesFarePaxNameDetails) &&
          !isEmpty(
            res.Root.Result.SeriesFarePaxNameDetails.SeriesFarePaxNameDetail,
          )
        ) {
          const list = Array.isArray(
            res.Root.Result.SeriesFarePaxNameDetails.SeriesFarePaxNameDetail,
          )
            ? res.Root.Result.SeriesFarePaxNameDetails.SeriesFarePaxNameDetail
            : [
                res.Root.Result.SeriesFarePaxNameDetails
                  .SeriesFarePaxNameDetail,
              ];
          allResultsRef.current = list || [];
          setSearchResults(searchText);
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <>
      {alert && (
        <AppAlert
          showIcon
          variant={alert.variant}
          title={alert.title}
          description={alert.description}
          onHide={() => {
            setAlert(undefined);
          }}
        />
      )}
      <AppLoading isLoading={isLoading} />
      <div className="coupons-container p-1">
        <Row className="spacing-8 my-2">
          <Col lg={3} md={3} sm={6} xs={12}>
            <AppDatePicker
              label="From Date"
              labelClassName="agent-series-fare_date-label"
              customClassName="agent-series-fare_date-picker"
              placeholder=""
              selectedDate={fromDate}
              onChange={(date) => setFromDate(date)}
            />
          </Col>
          <Col lg={3} md={3} sm={6} xs={12}>
            <AppDatePicker
              label="To Date"
              labelClassName="agent-series-fare_date-label"
              customClassName="agent-series-fare_date-picker"
              placeholder=""
              selectedDate={toDate}
              onChange={(date) => setToDate(date)}
            />
          </Col>
          <Col
            lg={6}
            md={6}
            sm={12}
            xs={12}
            className="d-flex align-center gap-4"
          >
            <AppButton
              btnText="Get Report"
              variant="secondary"
              onClick={handleGetSeriesTransactions}
            />
            <AppTextInput
              value={searchText}
              customClassName="flex-1"
              label="Enter keywords separated by comma(,)"
              onChange={handleSearch}
            />
          </Col>
        </Row>
        <div className="overflow-auto table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('txId')}
                  className="cursor-pointer"
                >
                  TxId
                </th>
                <th
                  onClick={() => handleSort('paxName')}
                  className="cursor-pointer"
                >
                  Pax Name
                </th>
                <th
                  onClick={() => handleSort('flightNumber')}
                  className="cursor-pointer"
                >
                  Flight No
                </th>
                <th
                  onClick={() => handleSort('sector')}
                  className="cursor-pointer"
                >
                  Sector
                </th>
                <th
                  onClick={() => handleSort('journeyDate')}
                  className="cursor-pointer"
                >
                  JDate
                </th>
                <th
                  onClick={() => handleSort('depTime')}
                  className="cursor-pointer"
                >
                  DepTime
                </th>
                <th
                  onClick={() => handleSort('bookingStatus')}
                  className="cursor-pointer"
                >
                  Booking Status
                </th>
                <th
                  onClick={() => handleSort('bookingDate')}
                  className="cursor-pointer"
                >
                  Booking Date
                </th>
                <th
                  onClick={() => handleSort('pnr')}
                  className="cursor-pointer"
                >
                  PNR
                </th>
                <th
                  onClick={() => handleSort('totalAmount')}
                  className="cursor-pointer"
                >
                  Amount
                </th>
                <th
                  onClick={() => handleSort('paxType')}
                  className="cursor-pointer"
                >
                  Pax Type
                </th>
                <th
                  onClick={() => handleSort('dob')}
                  className="cursor-pointer"
                >
                  Dob
                </th>
              </tr>
            </thead>
            <tbody>
              {shownResults.map((transaction) => (
                <tr key={transaction.TxId}>
                  <td>{transaction.TxId}</td>
                  <td>{`${transaction.Title} ${transaction.FirstName} ${transaction.LastName}`}</td>
                  <td>{`${transaction.Carrier}-${transaction.FlightNumberId}`}</td>
                  <td>{`${transaction.Origin}-${transaction.Destination}`}</td>
                  <td>{transaction.JourneyDate}</td>
                  <td>{transaction.DepatureTime}</td>
                  <td className="text-capitalize">
                    {transaction.BookingStatus}
                  </td>
                  <td>{transaction.BookingDate}</td>
                  <td>{transaction.PNR}</td>
                  <td>{transaction.TotalAmount}</td>
                  <td className="text-capitalize">{transaction.PaxType}</td>
                  <td>{transaction.DOB}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default SeriesTransactions;
